import { useCallback, useRef } from 'react'
import { buildErrorMessage, planImageCardAssetsRootGuid } from '@constants'
import { AxiosError } from 'axios'
import { useDebouncedCallback } from 'use-debounce'
import useUploadProjectFile from '@editor/hooks/requests/useUploadProjectFile'
import {
  completePlanImageCardInput,
  planImageCardDtoToPlanImageCard,
  planImageCardToCreateDTO,
  planImageCardToModifyDTO,
} from '@editor/stores/planImageCardStore/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import {
  clearPlanElementSelection,
  getSelectedPlanElement,
  usePlanImageCardStore,
} from '@editorStores'
import { getPlanImageCards } from '@queries'
import {
  createPlanImageCards,
  deletePlanImageCards,
  PlanImageCardCreateDTO,
  PlanImageCardDTO,
  updateFile,
  updatePlanImageCard,
} from '@mutations'
import { createPlaceholderImageFile } from 'src/domain/planImageCards/placeholder'
import type { PlanImageCard, PlanImageCardInput } from 'src/domain/planImageCards/types'
import { usePersistentParams } from 'src/hooks'

const PLAN_IMAGE_CARD_PERSIST_DEBOUNCE_MS = 500

const usePlanImageCardMutations = () => {
  const setCards = usePlanImageCardStore(state => state.setCards)
  const updateCardAttributes = usePlanImageCardStore(state => state.updateCardAttributes)
  const markPlaceholderCard = usePlanImageCardStore(state => state.markPlaceholderCard)
  const clearPlaceholderCard = usePlanImageCardStore(state => state.clearPlaceholderCard)
  const bumpImageContentVersion = usePlanImageCardStore(state => state.bumpImageContentVersion)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const uploadAsset = useUploadProjectFile(planImageCardAssetsRootGuid)

  const chainRef = useRef<Promise<unknown>>(Promise.resolve())

  const applyPlanImageCardResult = useCallback(
    (dtos: PlanImageCardDTO[]) => {
      const cards = dtos.map(planImageCardDtoToPlanImageCard)
      setCards(cards)

      const selectedPlanElement = getSelectedPlanElement()
      if (
        selectedPlanElement?.type === 'image-card' &&
        !cards.some(card => card.id === selectedPlanElement.id)
      ) {
        clearPlanElementSelection()
      }
    },
    [setCards],
  )

  const uploadPlanImageCardAsset = useCallback(
    async (file: File, name: string) => {
      const uploaded = await uploadAsset.mutateAsync({
        guid: '',
        name,
        parent_guid: planImageCardAssetsRootGuid,
        hierarchy_position: 0,
        type: 'file',
        element_guid: '',
        additional_data: {},
        file,
      })
      return uploaded.guid
    },
    [uploadAsset],
  )

  const createMutation = useMutation(
    (planImageCards: PlanImageCardCreateDTO[]) =>
      createPlanImageCards.request(projectId, planImageCards),
    {
      mutationKey: ['createPlanImageCards', projectId],
      meta: {
        title: 'Plan-Bild erstellen',
        description: 'Erstellt Plan-Bildkarten.',
      },
      onSuccess: planImageCards => applyPlanImageCardResult(planImageCards),
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen des Plan-Bildes'),
          { variant: 'error' },
        ),
    },
  )

  const updateMutation = useMutation(
    (id: string) => {
      const card = usePlanImageCardStore.getState().cards.find(entry => entry.id === id)
      if (!card) return Promise.resolve(null)
      return updatePlanImageCard.request(projectId, id, planImageCardToModifyDTO(card))
    },
    {
      mutationKey: ['updatePlanImageCard', projectId],
      meta: {
        title: 'Plan-Bild aktualisieren',
        description: 'Speichert Änderungen an einer Plan-Bildkarte.',
      },
      onError: async error => {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Aktualisieren des Plan-Bildes'),
          { variant: 'error' },
        )
        await queryClient.invalidateQueries({ queryKey: getPlanImageCards.getKey(projectId) })
      },
    },
  )

  const deleteMutation = useMutation(
    (ids: string[]) => deletePlanImageCards.request(projectId, ids),
    {
      mutationKey: ['deletePlanImageCards', projectId],
      meta: {
        title: 'Plan-Bild löschen',
        description: 'Löscht Plan-Bildkarten.',
      },
      onSuccess: planImageCards => applyPlanImageCardResult(planImageCards),
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Löschen des Plan-Bildes'),
          {
            variant: 'error',
          },
        ),
    },
  )

  const replaceImageMutation = useMutation(
    ({ id, file }: { id: string; file: File }) =>
      updateFile.request(projectId as string, planImageCardAssetsRootGuid, id, file),
    {
      mutationKey: ['replacePlanImageCardAsset', projectId],
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Ersetzen des Plan-Bildes'),
          { variant: 'error' },
        ),
    },
  )

  const { mutateAsync: runCreate } = createMutation
  const { mutateAsync: runUpdate } = updateMutation
  const { mutate: runDelete } = deleteMutation
  const { mutateAsync: runReplaceImage, isLoading: isReplacingImageAsset } = replaceImageMutation

  const createPlanImageCardFromInput = useCallback(
    async (input: PlanImageCardInput, options?: { placeholder?: boolean }) => {
      const previousIds = new Set(usePlanImageCardStore.getState().cards.map(card => card.id))
      const dto = planImageCardToCreateDTO(completePlanImageCardInput(input))
      const result = await runCreate([dto])
      const createdDto = result?.find((card: PlanImageCardDTO) => !previousIds.has(card.id))
      const created = createdDto ? planImageCardDtoToPlanImageCard(createdDto) : null
      if (created && options?.placeholder) {
        markPlaceholderCard(created.id)
      }
      return created
    },
    [markPlaceholderCard, runCreate],
  )

  const createPlanImageCardAtPosition = useCallback(
    async (
      position: { x: number; y: number; z: number },
      options: {
        file: File
        placeholder?: boolean
        name?: string
        planConfig?: PlanImageCard['planConfig']
      },
    ) => {
      const fileGuid = await uploadPlanImageCardAsset(
        options.file,
        options.name ?? options.file.name,
      )
      return createPlanImageCardFromInput(
        {
          position,
          fileGuid,
          name: options.name,
          planConfig: options.planConfig,
        },
        { placeholder: options.placeholder },
      )
    },
    [createPlanImageCardFromInput, uploadPlanImageCardAsset],
  )

  const createPlaceholderPlanImageCardAtPosition = useCallback(
    async (
      position: { x: number; y: number; z: number },
      planConfig?: PlanImageCard['planConfig'],
    ) => {
      const placeholderFile = await createPlaceholderImageFile()
      return createPlanImageCardAtPosition(position, {
        file: placeholderFile,
        placeholder: true,
        name: 'Plan-Bild Platzhalter',
        planConfig,
      })
    },
    [createPlanImageCardAtPosition],
  )

  const schedulePersist = useCallback(
    (id: string) => {
      const task = async () => {
        if (!usePlanImageCardStore.getState().cards.some(card => card.id === id)) return
        await runUpdate(id).catch(() => undefined)
      }

      chainRef.current = chainRef.current.catch(() => undefined).then(task)
    },
    [runUpdate],
  )

  const debouncedSchedulePersist = useDebouncedCallback(
    schedulePersist,
    PLAN_IMAGE_CARD_PERSIST_DEBOUNCE_MS,
  )

  const updatePlanImageCardFields = useCallback(
    (id: string, changes: Partial<PlanImageCard>) => {
      updateCardAttributes(id, changes)
      debouncedSchedulePersist(id)
    },
    [debouncedSchedulePersist, updateCardAttributes],
  )

  const replacePlanImageCardAsset = useCallback(
    async (id: string, file: File) => {
      await runReplaceImage({ id, file })
      clearPlaceholderCard(id)
      bumpImageContentVersion(id)
    },
    [bumpImageContentVersion, clearPlaceholderCard, runReplaceImage],
  )

  const deletePlanImageCardById = useCallback(
    (id: string) => {
      runDelete([id])
    },
    [runDelete],
  )

  return {
    createPlanImageCardFromInput,
    createPlanImageCardAtPosition,
    createPlaceholderPlanImageCardAtPosition,
    updatePlanImageCardFields,
    replacePlanImageCardAsset,
    deletePlanImageCard: deletePlanImageCardById,
    flushPlanImageCardPersist: () => debouncedSchedulePersist.flush(),
    isUploadingAsset: uploadAsset.isLoading,
    isReplacingImageAsset,
  }
}

export default usePlanImageCardMutations
